/**
 * Homeowner service search: typeahead synonyms and task-tile labels layered on the catalog mirror
 * in `mockData/allTrades.js` (labels and groups live there). Slugs must match `docs/cosmos-seeds/`.
 * Everything in this file is web-only and never sent to the API.
 */

/** Catalog trades a homeowner can search, in tile order. `other` is handled as "Something else". */
export const SEARCH_TRADE_IDS = ['plumbing', 'electrical', 'hvac', 'landscaping'];

/** Example jobs shown under each trade tile, so people recognize their problem at a glance. */
export const TRADE_EXAMPLE_JOBS = {
  plumbing: 'Leaks, clogs, water heaters',
  electrical: 'Outlets, lights, breakers',
  hvac: 'AC, heat, tune-ups',
  landscaping: 'Lawns, trees, patios',
  other: 'Tell us what you need',
};

/** Words people type when they mean the whole trade (typeahead suggests the trade's task list). */
export const TRADE_SEARCH_SYNONYMS = {
  plumbing: ['plumber', 'plumbing', 'pipes'],
  electrical: ['electrician', 'electrical', 'electric', 'wiring'],
  hvac: ['hvac', 'heating', 'cooling', 'heating and cooling', 'air conditioning'],
  landscaping: ['landscaper', 'landscaping', 'lawn care', 'yard work', 'yard'],
};

/** Extra phrases per catalog sub-service, matched alongside its catalog label. */
export const SUBSERVICE_SEARCH_SYNONYMS = {
  // Plumbing
  'clogged-drain': ['clogged drain', 'drain', 'clog', 'slow drain', 'backed up sink', 'shower drain', 'tub drain', 'drain snake'],
  'leaking-pipe': ['leak', 'leaking pipe', 'burst pipe', 'dripping faucet', 'water leak', 'pipe repair'],
  'toilet-issue': ['toilet', 'running toilet', 'toilet clog', 'toilet leak', 'toilet won\'t flush'],
  'water-heater': ['water heater', 'no hot water', 'hot water heater', 'tankless water heater'],
  'faucet-install': ['faucet', 'new faucet', 'sink install', 'replace sink'],
  'garbage-disposal': ['garbage disposal', 'disposal', 'disposer'],

  // Electrical
  'install-outlet': ['new outlet', 'add outlet', 'outlet install', 'usb outlet', 'gfci'],
  'install-ceiling-fan': ['ceiling fan', 'light fixture', 'chandelier', 'install fan'],
  'install-switch-dimmer': ['dimmer', 'light switch install', 'smart switch'],
  'install-outdoor-lighting': ['outdoor lights', 'security light', 'motion light', 'porch light'],
  'install-dedicated-circuit': ['dedicated circuit', 'new circuit', '240v', 'appliance circuit'],
  'panel-upgrade': ['panel upgrade', 'breaker box', 'electrical panel', 'fuse box', '200 amp'],
  'ev-charger': ['ev charger', 'car charger', 'tesla charger', 'level 2 charger'],
  'smoke-co-detector': ['smoke detector', 'carbon monoxide', 'co detector', 'smoke alarm'],
  'generator-install': ['generator', 'standby generator', 'backup power', 'generac'],
  'outlet-not-working': ['outlet not working', 'dead outlet', 'sparking outlet', 'outlet sparks'],
  'light-switch-not-working': ['light switch not working', 'switch broken', 'switch not working'],
  'breaker-tripping': ['breaker tripping', 'breaker keeps tripping', 'tripped breaker'],
  'power-out-partial': ['power out', 'no power', 'lost power', 'half the house'],
  'lights-flickering': ['flickering lights', 'lights dimming', 'flicker'],
  'safety-inspection': ['electrical inspection', 'safety inspection', 'home inspection electrical'],
  'panel-assessment': ['panel assessment', 'panel inspection', 'old panel'],

  // HVAC
  'ac-not-cooling': ['ac not cooling', 'ac broken', 'air conditioner', 'no ac', 'ac repair', 'not cooling', 'warm air'],
  'heat-not-working': ['no heat', 'heat not working', 'furnace not working', 'furnace repair', 'heater broken', 'cold house'],
  'smell-sound': ['strange noise', 'burning smell', 'furnace noise', 'hvac smell'],
  'airflow-vents': ['weak airflow', 'vents', 'ductwork', 'uneven temperature', 'hot room'],
  'tune-up': ['tune up', 'hvac maintenance', 'furnace cleaning', 'ac service', 'filter change'],
  'thermostat-install': ['thermostat', 'smart thermostat', 'nest', 'ecobee'],
  'ac-install': ['new ac', 'ac install', 'replace ac', 'central air', 'heat pump'],
  'furnace-install': ['new furnace', 'furnace install', 'replace furnace', 'boiler'],

  // Landscaping
  'mow-edge-trim': ['mow', 'mowing', 'lawn mowing', 'grass cutting', 'cut grass', 'edging'],
  'aerate-lawn': ['aerate', 'aeration', 'core aeration'],
  'overseed-reseed': ['overseed', 'reseed', 'grass seed', 'bare spots'],
  'new-sod-turf': ['sod', 'new lawn', 'turf', 'artificial turf'],
  'fertilize-lawn': ['fertilize', 'fertilizer', 'lawn treatment'],
  'weed-control': ['weeds', 'weed control', 'weed killer', 'dandelions'],
  'pest-grub': ['grubs', 'lawn pests', 'grub control'],
  'trim-prune-shrubs': ['trim bushes', 'prune', 'hedge trimming', 'shrubs'],
  'tree-trimming': ['tree trimming', 'branches', 'tree pruning', 'limb removal'],
  'remove-tree-stump': ['tree removal', 'stump', 'stump grinding', 'cut down tree'],
  'plant-trees-shrubs': ['plant trees', 'planting', 'new shrubs'],
  'leaf-removal': ['leaves', 'leaf cleanup', 'fall cleanup', 'raking'],
  'debris-brush': ['brush removal', 'yard debris', 'haul away branches', 'yard cleanup'],
  'mulch-beds': ['mulch', 'mulching', 'bed refresh'],
  'flower-beds': ['flower beds', 'flowers', 'garden bed', 'plant flowers'],
  'garden-edging': ['bed edging', 'garden border', 'landscape edging'],
  'decorative-rock': ['rock', 'river rock', 'gravel', 'stone beds'],
  'grade-level': ['grading', 'level yard', 'low spots', 'regrade'],
  'drainage': ['drainage', 'french drain', 'standing water', 'downspout', 'yard flooding'],
  'install-sprinkler': ['sprinkler system', 'irrigation install', 'new sprinklers'],
  'repair-sprinkler': ['sprinkler repair', 'broken sprinkler head', 'sprinkler leak'],
  'winterization': ['winterize sprinklers', 'sprinkler blowout', 'blow out sprinklers'],
  'spring-irrigation': ['sprinkler startup', 'turn on sprinklers', 'spring startup'],
  'patio-walkway': ['patio', 'walkway', 'pavers', 'stone path', 'hardscape'],
  'retaining-wall': ['retaining wall', 'block wall', 'stone wall'],
  'landscape-lighting': ['landscape lighting', 'path lights', 'yard lights'],
  'fire-pit': ['fire pit', 'firepit', 'outdoor fireplace'],
  'pergola-shade': ['pergola', 'shade structure', 'arbor', 'shade sail'],
};

/** Task-step group order where it differs from the catalog: urgent repairs first. */
export const SEARCH_TASK_GROUP_ORDER = {
  electrical: ['electrical-troubleshooting', 'electrical-installation', 'electrical-safety'],
};

/**
 * Short labels for the task tiles only. The group heading carries the verb ("Installs &
 * upgrades" → "EV charger"), so tiles fit one line on desktop and two on phones. Chips, lead
 * emails and the dashboard keep the full catalog label. Keyed by slug, same as the API.
 */
export const SUBSERVICE_TILE_LABELS = {
  // Plumbing
  'clogged-drain': 'Clogged drain or sink',
  'leaking-pipe': 'Leaking pipe or faucet',
  'toilet-issue': "Toilet running or won't flush",
  'garbage-disposal': 'Broken garbage disposal',
  'water-heater': 'Water heater',
  'faucet-install': 'New faucet or sink',
  // Electrical
  'outlet-not-working': 'Dead or sparking outlet',
  'light-switch-not-working': 'Switch not working',
  'breaker-tripping': 'Breaker keeps tripping',
  'power-out-partial': 'Power out in some rooms',
  'lights-flickering': 'Flickering or dim lights',
  'install-outlet': 'New outlet',
  'install-ceiling-fan': 'Ceiling fan or light',
  'install-switch-dimmer': 'Switch or dimmer',
  'install-outdoor-lighting': 'Outdoor or security lights',
  'install-dedicated-circuit': 'Circuit for an appliance',
  'panel-upgrade': 'Panel upgrade',
  'ev-charger': 'EV charger',
  'smoke-co-detector': 'Smoke or CO detectors',
  'generator-install': 'Generator',
  'safety-inspection': 'Safety inspection',
  'panel-assessment': 'Panel assessment',
  // HVAC
  'ac-not-cooling': 'AC not cooling',
  'heat-not-working': 'Heat not working',
  'smell-sound': 'Strange smell or noise',
  'airflow-vents': 'Weak airflow or vents',
  'tune-up': 'Tune-up or maintenance',
  'thermostat-install': 'New thermostat',
  'ac-install': 'New AC unit',
  'furnace-install': 'New furnace or heating',
  // Landscaping
  'mow-edge-trim': 'Mowing and trimming',
  'aerate-lawn': 'Aeration',
  'overseed-reseed': 'Overseed bare spots',
  'new-sod-turf': 'New sod or turf',
  'fertilize-lawn': 'Fertilizing',
  'weed-control': 'Weed control',
  'pest-grub': 'Pest or grub control',
  'trim-prune-shrubs': 'Trim shrubs or bushes',
  'tree-trimming': 'Tree trimming',
  'remove-tree-stump': 'Tree or stump removal',
  'plant-trees-shrubs': 'Plant trees or shrubs',
  'leaf-removal': 'Leaf cleanup',
  'debris-brush': 'Brush and debris removal',
  'mulch-beds': 'Mulch beds',
  'flower-beds': 'Flower beds',
  'garden-edging': 'Bed edging or borders',
  'decorative-rock': 'Decorative rock or stone',
  'grade-level': 'Level yard or low spots',
  drainage: 'Drainage or French drain',
  'install-sprinkler': 'New sprinkler system',
  'repair-sprinkler': 'Sprinkler repair',
  winterization: 'Winterize sprinklers',
  'spring-irrigation': 'Spring sprinkler startup',
  'patio-walkway': 'Patio or walkway',
  'retaining-wall': 'Retaining wall',
  'landscape-lighting': 'Landscape lighting',
  'fire-pit': 'Fire pit',
  'pergola-shade': 'Pergola or shade structure',
};
